import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Link
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ArrowForward from '@mui/icons-material/ArrowForward';
import BarChart from '@mui/icons-material/BarChart';
import Check from '@mui/icons-material/Check';
import Login from '@mui/icons-material/Login';
import SmartToy from '@mui/icons-material/SmartToy';
import EmojiEvents from '@mui/icons-material/EmojiEvents';
import Videocam from '@mui/icons-material/Videocam';
import Groups from '@mui/icons-material/Groups';
import { ResponsiveContainer, LineChart, Line, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { SiteNavigation } from '../components/SiteNavigation';
import { HeroTrackingOverlay } from '../components/HeroTrackingOverlay';
import { AuthDialog } from '../components/AuthDialog';
import GroupDiscovery from '../components/GroupDiscovery';
import { useAuth } from '../contexts/AuthContext';
import { useFirebase } from '../contexts/FirebaseContext';
import { afterHoursTokens, displayFont } from '../theme';
import { APP_STORE_URL, CONTACT_EMAIL } from '../constants/links';
import logger from '../utils/logger';
import releaseClaims from '../constants/releaseClaims.json';
import { useNavigate } from 'react-router-dom';


// Owner-neutral on purpose: the JuggleVision marks are not held by lcCode, llc. Keep in step with
// the footer line in public/support.html, privacy-public.html and terms-public.html.
const TRADEMARK_NOTICE =
  'JuggleVision and the JuggleVision logo are trademarks. Apple, the Apple logo, App Store, Apple ID, Face ID, iPad, iPhone and Touch ID are trademarks of Apple Inc., registered in the U.S. and other countries and regions. App Store is a service mark of Apple Inc. IOS is a trademark or registered trademark of Cisco in the U.S. and other countries and is used under license. Google, the Google logo and Firebase are trademarks of Google LLC. Other names are the property of their respective owners.';

// The hero sits on dusk photography in both colour modes, so its copy keeps fixed light colours.
const HERO_TEXT = afterHoursTokens.dark.text;
const HERO_MUTED = '#C3CAD5';

const pageGutter = { xs: 3, sm: 4, md: 7 };
const pageWidth = 1440;

const steps = [
  { title: 'Download the app', copy: 'Install JuggleVision from the App Store.' },
  { title: 'Connect your account', copy: 'Sign in to sync your progress.' },
  { title: 'Compete worldwide', copy: 'Join leaderboards and track stats.' }
];

const features = [
  {
    icon: SmartToy,
    title: releaseClaims.aiLabel,
    copy: releaseClaims.aiDescription
  },
  {
    icon: EmojiEvents,
    title: 'Global Leaderboards',
    copy: 'Compete with players worldwide across multiple categories: total juggles, best sessions, longest sessions, and juggling rates.'
  },
  {
    icon: Videocam,
    title: 'Custom Video Overlays',
    copy: 'Record with a basic score overlay for free. Plus adds custom overlay positioning.'
  }
];

const plans = [
  {
    name: 'Free',
    price: '$0',
    features: releaseClaims.freeFeatures,
    popular: false
  },
  {
    name: 'Plus',
    price: `$${releaseClaims.plusMonthlyUSD}`,
    features: releaseClaims.plusFeatures,
    popular: true
  }
];

// Illustrative only: the chart shows the shape of a progress view, not real player data.
const sampleSessions = [28, 40, 57, 45, 72, 80, 70, 94, 98, 115, 124].map((count, i) => ({ session: i + 1, count }));

const Eyebrow: React.FC<{ children: React.ReactNode; color?: string }> = ({ children, color }) => (
  <Typography variant="overline" component="p" sx={{ display: 'block', color: color || 'text.secondary', mb: 2 }}>
    {children}
  </Typography>
);

const MainApp: React.FC = () => {
  const { user } = useAuth();
  const { auth } = useFirebase();
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const tokens = afterHoursTokens[theme.palette.mode];

  const handleSignInClick = () => {
    setAuthDialogOpen(true);
  };

  const handleAuthClose = () => {
    setAuthDialogOpen(false);
  };

  const handleSignOut = async () => {
    try {
      logger.log('🔐 Signing out user...');

      // Session cookies no longer used in single domain architecture

      // Then sign out of Firebase
      await auth.signOut();
      logger.log('✅ Firebase sign out successful');

      // Optional: Clear browser cache/cookies for clean state
      if (window.localStorage) {
        Object.keys(window.localStorage).forEach(key => {
          if (key.startsWith('firebase:')) {
            window.localStorage.removeItem(key);
          }
        });
      }
    } catch (error) {
      logger.error('❌ Sign out failed:', error);
    }
  };

  const sectionHeading = {
    fontSize: { xs: '2.5rem', md: '2.75rem' },
    mb: 2
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', textAlign: 'left' }}>
      <Box
        component="a"
        href="#main"
        sx={{
          position: 'fixed',
          left: 16,
          top: -80,
          zIndex: 1300,
          px: 2,
          py: 1.5,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          borderRadius: 1,
          textDecoration: 'none',
          '&:focus': { top: 12 }
        }}
      >
        Skip to content
      </Box>

      <SiteNavigation currentSite="main" onSignOut={handleSignOut} onSignIn={handleSignInClick} />

      <Box component="main" id="main">
        {/* Hero */}
        <Box
          component="section"
          aria-labelledby="hero-heading"
          sx={{
            position: 'relative',
            isolation: 'isolate',
            overflow: 'hidden',
            bgcolor: afterHoursTokens.dark.header,
            color: HERO_TEXT,
            minHeight: { md: 640 },
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Box
            sx={{
              position: { xs: 'relative', md: 'absolute' },
              inset: { md: 0 },
              height: { xs: 360, sm: 440, md: '100%' },
              order: { xs: 2, md: 0 },
              overflow: 'hidden',
              isolation: 'isolate',
              zIndex: { xs: 0, md: -1 }
            }}
          >
            <Box component="picture">
              <source media="(max-width: 899px)" srcSet="/after-hours/hero-fc-v2-960.webp" type="image/webp" />
              <source srcSet="/after-hours/hero-fc-v2-1672.webp" type="image/webp" />
              <Box
                component="img"
                src="/after-hours/hero-fc-v2-1672.jpg"
                alt="A young soccer player in the JuggleVision FC kit practicing keep-ups on a floodlit pitch at dusk"
                {...({ fetchpriority: 'high' } as Record<string, string>)}
                sx={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: { xs: '100% center', md: 'center 52%' },
                  zIndex: -2
                }}
              />
            </Box>
            <Box
              aria-hidden="true"
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: -1,
                background: {
                  xs: 'linear-gradient(180deg, rgba(8,13,17,0.9) 0%, rgba(8,13,17,0) 20%, rgba(8,13,17,0) 80%, rgba(8,13,17,0.45) 100%)',
                  md: 'linear-gradient(90deg, rgba(8,13,17,0.78) 0%, rgba(8,13,17,0.35) 48%, rgba(8,13,17,0) 72%)'
                }
              }}
            />
            <HeroTrackingOverlay />
          </Box>
          <Box
            sx={{
              maxWidth: pageWidth,
              width: '100%',
              mx: 'auto',
              px: pageGutter,
              pt: { xs: 4, md: 10 },
              pb: { xs: 3, md: 6 },
              position: 'relative'
            }}
          >
            <Eyebrow color="#ADB7C5">Practice anywhere</Eyebrow>
            <Typography
              variant="h1"
              id="hero-heading"
              sx={{
                fontSize: { xs: '4rem', sm: '6rem', md: 'clamp(6rem, 7.6vw, 7.5rem)' },
                textTransform: 'uppercase',
                maxWidth: 760,
                color: HERO_TEXT
              }}
            >
              Every<br />touch counts
              <Box component="span" sx={{ color: afterHoursTokens.dark.link }}>.</Box>
            </Typography>
            <Typography
              sx={{
                mt: 2.5,
                fontSize: { xs: '1.125rem', md: '1.5rem' },
                lineHeight: 1.45,
                color: '#E4E7EE',
                maxWidth: 560
              }}
            >
              Count every touch. See your progress.
              <Box component="br" sx={{ display: { xs: 'none', sm: 'inline' } }} />{' '}
              Compete with players worldwide.
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: 2,
                mt: 4,
                maxWidth: { xs: 360, sm: 'none' }
              }}
            >
              <Button
                component="a"
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                sx={{ minHeight: 60, px: 3.5 }}
              >
                Get the iOS app
              </Button>
              {user ? (
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/stats')}
                  startIcon={<BarChart />}
                  sx={{
                    minHeight: 60,
                    color: HERO_TEXT,
                    borderColor: alpha(HERO_TEXT, 0.45),
                    '&:hover': { borderColor: HERO_TEXT, backgroundColor: alpha(HERO_TEXT, 0.08) }
                  }}
                >
                  View your stats
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleSignInClick}
                  startIcon={<Login />}
                  sx={{
                    minHeight: 60,
                    color: HERO_TEXT,
                    borderColor: alpha(HERO_TEXT, 0.45),
                    '&:hover': { borderColor: HERO_TEXT, backgroundColor: alpha(HERO_TEXT, 0.08) }
                  }}
                >
                  Sign in
                </Button>
              )}
            </Box>
            <Typography sx={{ mt: 2, fontSize: '0.8125rem', color: HERO_MUTED }}>
              Free to download on the App Store
            </Typography>
          </Box>
          <Typography
            aria-hidden="true"
            sx={{
              display: { xs: 'none', lg: 'block' },
              position: 'absolute',
              right: 48,
              top: 34,
              fontSize: '0.625rem',
              fontWeight: 500,
              lineHeight: 1.65,
              letterSpacing: '0.3em',
              textTransform: 'uppercase',
              borderLeft: '1px solid #8F9CAC',
              pl: 2.5,
              color: HERO_TEXT
            }}
          >
            A session<br />worth keeping.
          </Typography>
        </Box>

        {/* Get started */}
        <Box
          component="section"
          id="get-started"
          aria-labelledby="get-started-heading"
          sx={{ borderBottom: 1, borderColor: 'divider', scrollMarginTop: 24 }}
        >
          <Typography id="get-started-heading" component="h2" className="sr-only">
            Get started in 3 steps
          </Typography>
          <Box
            component="ol"
            sx={{
              listStyle: 'none',
              m: 0,
              maxWidth: pageWidth,
              mx: 'auto',
              px: pageGutter,
              py: { xs: 1, md: 4.5 },
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }
            }}
          >
            {steps.map((step, index) => (
              <Box
                component="li"
                key={step.title}
                sx={{
                  display: 'flex',
                  gap: 3,
                  alignItems: 'flex-start',
                  py: { xs: 3, md: 0 },
                  px: { md: 3 },
                  '&:first-of-type': { pl: { md: 0 } },
                  '& + &': {
                    borderTop: { xs: 1, md: 0 },
                    borderLeft: { xs: 0, md: 1 },
                    borderColor: 'divider'
                  }
                }}
              >
                <Typography
                  aria-hidden="true"
                  sx={{ fontFamily: displayFont, fontWeight: 700, fontSize: '2.5rem', lineHeight: 1, color: 'text.secondary' }}
                >
                  0{index + 1}
                </Typography>
                <Box>
                  <Typography component="h3" sx={{ fontFamily: displayFont, fontWeight: 700, fontSize: '1.75rem', lineHeight: 1, mb: 1 }}>
                    {step.title}
                  </Typography>
                  <Typography sx={{ color: 'text.secondary', fontSize: '0.9375rem', lineHeight: 1.6 }}>
                    {step.copy}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Features */}
        <Box
          component="section"
          id="features"
          aria-labelledby="features-heading"
          sx={{ maxWidth: pageWidth, mx: 'auto', px: pageGutter, pt: { xs: 6, md: 9 }, pb: { xs: 4, md: 6 }, scrollMarginTop: 24 }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '0.95fr 1.3fr' },
              gap: { xs: 4, md: 8 },
              alignItems: 'center'
            }}
          >
            <Box>
              <Eyebrow>Your journey</Eyebrow>
              <Typography variant="h2" id="features-heading" sx={sectionHeading}>
                Your progress,<br />session by session.
              </Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: { xs: '1rem', md: '1.0625rem' }, lineHeight: 1.65, maxWidth: 440 }}>
                AI counting (beta), global leaderboards, and professional video recording.
                Track your progress and compete with players worldwide.
              </Typography>
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: 'text.secondary', fontSize: '0.8125rem', mb: 1 }}>
                <span>Touches per session</span>
                <Box component="span" sx={{ color: tokens.progress, fontWeight: 600 }}>Keep going.</Box>
              </Box>
              <Box sx={{ height: { xs: 180, md: 200 } }} aria-hidden="true">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={sampleSessions} margin={{ top: 10, right: 9, left: -23, bottom: 5 }}>
                    <CartesianGrid stroke={tokens.line} vertical={false} />
                    <YAxis
                      tick={{ fill: tokens.muted, fontSize: 12 }}
                      domain={[0, 150]}
                      ticks={[0, 50, 100, 150]}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      cursor={{ stroke: tokens.line }}
                      content={({ active, payload }) =>
                        active && payload && payload.length ? (
                          <Box sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1.5, px: 2, py: 1.5, fontSize: '0.75rem' }}>
                            Session {payload[0].payload.session}
                            <Box component="strong" sx={{ display: 'block', fontSize: '1rem' }}>{payload[0].value} touches</Box>
                          </Box>
                        ) : null
                      }
                    />
                    <Line
                      type="linear"
                      dataKey="count"
                      stroke={tokens.progress}
                      strokeWidth={3}
                      dot={{ r: 4, fill: tokens.progress, strokeWidth: 0 }}
                      activeDot={{ r: 6, fill: tokens.link }}
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
              <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', textAlign: 'right' }}>
                Illustrative session data
              </Typography>
            </Box>
          </Box>

          <Box
            component="ul"
            sx={{
              listStyle: 'none',
              p: 0,
              m: 0,
              mt: { xs: 5, md: 7 },
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: { xs: 2, md: 3 }
            }}
          >
            {features.map(({ icon: Icon, title, copy }) => (
              <Card component="li" key={title} sx={{ height: '100%' }}>
                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                  <Icon sx={{ fontSize: 36, color: tokens.link, mb: 2 }} />
                  <Typography component="h3" sx={{ fontFamily: displayFont, fontWeight: 700, fontSize: '1.75rem', lineHeight: 1.1, mb: 1.5 }}>
                    {title}
                  </Typography>
                  <Typography sx={{ color: 'text.secondary', fontSize: '0.9375rem', lineHeight: 1.65 }}>
                    {copy}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>

        {/* Group Discovery for Authenticated Users */}
        {releaseClaims.webTeamsAvailable && user && (
          <Box
            component="section"
            aria-labelledby="groups-heading"
            sx={{ maxWidth: pageWidth, mx: 'auto', px: pageGutter, py: { xs: 4, md: 6 } }}
          >
            <Card>
              <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                <Eyebrow>Your people</Eyebrow>
                <Typography
                  variant="h2"
                  id="groups-heading"
                  sx={{ ...sectionHeading, display: 'flex', alignItems: 'center', gap: 1.5 }}
                >
                  <Groups sx={{ fontSize: '2.25rem', color: tokens.link }} />
                  Join groups &amp; teams
                </Typography>
                <GroupDiscovery />
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Subscription Tiers */}
        <Box
          component="section"
          id="pricing"
          aria-labelledby="pricing-heading"
          sx={{ maxWidth: pageWidth, mx: 'auto', px: pageGutter, py: { xs: 6, md: 9 }, scrollMarginTop: 24 }}
        >
          <Eyebrow>Plans</Eyebrow>
          <Typography variant="h2" id="pricing-heading" sx={sectionHeading}>
            Choose your plan.
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: { xs: '1rem', md: '1.0625rem' }, mb: { xs: 4, md: 5 } }}>
            From basic tracking to professional features
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: { xs: 2, md: 3 },
              maxWidth: 920
            }}
          >
            {plans.map(plan => (
              <Card
                key={plan.name}
                sx={{
                  position: 'relative',
                  ...(plan.popular && { borderWidth: 2, borderColor: 'primary.main' })
                }}
              >
                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1 }}>
                    <Typography component="h3" sx={{ fontFamily: displayFont, fontWeight: 700, fontSize: '2rem', lineHeight: 1 }}>
                      {plan.name}
                    </Typography>
                    {plan.popular && (
                      <Chip label="POPULAR" color="primary" size="small" sx={{ fontWeight: 600, letterSpacing: '0.08em', fontSize: '0.6875rem' }} />
                    )}
                  </Box>
                  <Typography sx={{ fontFamily: displayFont, fontWeight: 800, fontSize: { xs: '3.25rem', md: '3.75rem' }, lineHeight: 1, my: 2 }}>
                    {plan.price}
                    <Typography component="span" sx={{ fontFamily: 'inherit', fontWeight: 700, fontSize: '1.375rem', color: 'text.secondary', ml: 0.5 }}>
                      /month
                    </Typography>
                  </Typography>
                  <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, borderTop: 1, borderColor: 'divider', pt: 2 }}>
                    {plan.features.map(feature => (
                      <Box component="li" key={feature} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75, fontSize: '0.9375rem' }}>
                        <Check sx={{ fontSize: 20, color: tokens.link }} />
                        {feature}
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: pageWidth, mx: 'auto', px: pageGutter, pb: 3 }}>
          {releaseClaims.priceNote} Videos stay on your device; signed-in practice history syncs with your account.
        </Typography>

        {/* Call to Action */}
        <Box sx={{ maxWidth: pageWidth, mx: 'auto', px: pageGutter }}>
          <Box
            component="section"
            aria-labelledby="closing-heading"
            sx={{
              borderTop: 1,
              borderColor: 'divider',
              py: { xs: 5, md: 7 },
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              gap: 3
            }}
          >
            <Box>
              <Eyebrow>Same ball. A better you.</Eyebrow>
              <Typography variant="h2" id="closing-heading" sx={{ fontSize: { xs: '2.375rem', md: '2.875rem' } }}>
                Make the next session yours.
              </Typography>
            </Box>
            <Button
              component="a"
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              size="large"
              endIcon={<ArrowForward />}
              sx={{ minHeight: 60, px: 3.5 }}
            >
              Download now
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Footer */}
      <Box component="footer" sx={{ borderTop: 1, borderColor: 'divider', bgcolor: tokens.header }}>
        <Box
          sx={{
            maxWidth: pageWidth,
            mx: 'auto',
            px: pageGutter,
            py: { xs: 4, md: 3.5 },
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: { xs: 2, md: 3.5 }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box
              component="img"
              src="/app-icon.png"
              alt=""
              sx={{ width: 32, height: 32, borderRadius: 1.5 }}
            />
            <Typography sx={{ fontWeight: 600, fontSize: '1.25rem' }}>
              JuggleVision<Box component="sup" aria-hidden="true" sx={{ fontSize: '0.5em', fontWeight: 500, ml: '1px' }}>™</Box>
            </Typography>
          </Box>
          <Typography sx={{ color: 'text.secondary', fontSize: '0.8125rem', display: { xs: 'none', md: 'block' } }}>
            Every touch counts.
          </Typography>
          <Box
            component="nav"
            aria-label="Footer"
            sx={{
              ml: { md: 'auto' },
              display: 'flex',
              flexWrap: 'wrap',
              columnGap: 3.5,
              rowGap: 1,
              fontSize: '0.875rem',
              '& a': { color: 'text.secondary', textDecoration: 'none', py: 1 },
              '& a:hover': { color: tokens.link, textDecoration: 'underline' }
            }}
          >
            <Link href="/support.html">Support</Link>
            <Link href="/privacy-public.html">Privacy</Link>
            <Link href="/terms-public.html">Terms</Link>
            <Link href={`mailto:${CONTACT_EMAIL}`}>Contact</Link>
          </Box>
          <Typography sx={{ flexBasis: '100%', fontSize: '0.75rem', color: 'text.secondary' }}>
            © {new Date().getFullYear()} lcCode, llc
          </Typography>
          <Typography sx={{ flexBasis: '100%', fontSize: '0.6875rem', lineHeight: 1.6, color: 'text.secondary', maxWidth: 880 }}>
            {TRADEMARK_NOTICE}
          </Typography>
        </Box>
      </Box>

      {/* Authentication Dialog */}
      <AuthDialog
        open={authDialogOpen}
        onClose={handleAuthClose}
        auth={auth}
      />
    </Box>
  );
};

export default MainApp;
